import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { useMembershipDialog } from '@/contexts/MembershipDialogContext';
import { MEMBERSHIP_SOURCES, type MembershipSource } from '@/hooks/useOpenMembership';
import { getRememberedChatPath } from '@/utils/lastChatPath';
import PageLoader from '@/ui-library/PageLoader';
import { rememberPendingSession } from './checkoutPlanStorage';

const parseSource = (value: string | null): MembershipSource | null =>
  MEMBERSHIP_SOURCES.find((source) => source === value) ?? null;

/**
 * `/membership` — deep links, the partner-embed handoff, and Stripe's return_url.
 * Opens the support sheet and lands the voter in chat underneath it.
 */
export default function MembershipLink() {
  const { isAuthenticated, isInitializing } = useAuth();
  const dialog = useMembershipDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handled = useRef(false);
  const sessionId = searchParams.get('session_id');

  // Saved before the URL is replaced, so a reload mid-check can reopen it (see the provider).
  useEffect(() => {
    if (sessionId) rememberPendingSession(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (isInitializing || handled.current) return;
    handled.current = true;
    dialog?.openMembership({
      source: parseSource(searchParams.get('source')),
      sessionId,
    });
    const stored = getRememberedChatPath();
    navigate(
      isAuthenticated ? (stored?.startsWith('/chat') ? stored : '/chat') : '/chat?guest=true',
      { replace: true }
    );
  }, [dialog, isAuthenticated, isInitializing, navigate, searchParams, sessionId]);

  return <PageLoader />;
}
